import React, { useState, useEffect, useCallback } from 'react';
// Ajuste os caminhos conforme a estrutura do seu projeto
import { EmpreendimentoEditPayload, ParteEmpreendimentoCreate, ParteEmpreendimento } from '../../../../types/empreendimento'; 
import { EmpreendimentoService } from '../../../../services/empreendimentoService'; 

// Inicializa o serviço fora do componente
// NOTA: Certifique-se que o empreendimentoService possui o método 'updateParteEmpreendimento(id, payload)'
const empreendimentoService = new EmpreendimentoService(); 

interface SelectOption { id: number | string; nome: string; }

// --- INTERFACES DE TIPO PARA TRATAR A RESPOSTA DA API ---
interface PaginatedResponse<T> {
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
}

type ParteResponse = PaginatedResponse<ParteEmpreendimento> | ParteEmpreendimento[];
// ------------------------------------------------------------


interface EmpreendimentoTipologiasFormProps {
    empreendimentoId: number; 
    
    // Os campos abaixo podem não ser mais necessários se o foco for só nas Partes/Unidades
    formData: EmpreendimentoEditPayload;
    handleInputChange: (field: string, value: any) => void;
    errors: Record<string, string>;
    
    tiposParteOptions: SelectOption[];
    disponibilidadeOptions: SelectOption[];
}

// Função utilitária para formatar números como moeda
const formatCurrency = (value: string | number | undefined, decimals = 2): string => {
    if (value === undefined || value === null || value === "") return 'R$ 0,00';
    const num = typeof value === 'string' ? parseFloat(String(value).replace(',', '.')) : value;
    if (isNaN(num)) return 'R$ -';

    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: decimals,
    }).format(num);
};

// Função utilitária para formatar texto de atributo
const formatAttributeLabel = (key: string): string => {
    if (!key) return '';
    return key.replace(/_/g, ' ')
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
}


const EmpreendimentoTipologiasForm: React.FC<EmpreendimentoTipologiasFormProps> = ({
    empreendimentoId,
    formData, // Mantido, mas não usado diretamente para Partes
    handleInputChange, // Mantido, mas não usado diretamente para Partes
    errors, // Mantido
    tiposParteOptions,
    disponibilidadeOptions,
}) => {
    const [partes, setPartes] = useState<ParteEmpreendimento[]>([]);
    const [loadingPartes, setLoadingPartes] = useState(true);
    const [errorPartes, setErrorPartes] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false); // Novo estado para o salvamento

    // 1. Lógica de Carregamento (Função idêntica à anterior)
    const loadPartes = useCallback(async () => {
        if (!empreendimentoId) return;
        setLoadingPartes(true);
        setErrorPartes(null);
        try {
            const response: ParteResponse = await empreendimentoService.listPartesEmpreendimento(empreendimentoId);
            
            let loadedPartes: ParteEmpreendimento[];
            
            if ('results' in response && Array.isArray(response.results)) {
                loadedPartes = response.results;
            } else if (Array.isArray(response)) {
                loadedPartes = response;
            } else {
                loadedPartes = []; 
            }

            setPartes(loadedPartes);
        } catch (err) {
            console.error("Erro ao carregar unidades:", err);
            setErrorPartes("Não foi possível carregar as unidades do empreendimento.");
        } finally {
            setLoadingPartes(false);
        }
    }, [empreendimentoId]);

    useEffect(() => {
        loadPartes();
    }, [loadPartes]);
    

    // 2. Lógica de Edição: APENAS ATUALIZA O ESTADO LOCAL
    const handleParteChange = (index: number, field: keyof ParteEmpreendimentoCreate, value: any) => {
        // 1. Atualizar o estado local imediatamente
        const newPartes = [...partes];
        // Garantindo que 'value' seja do tipo correto para números
        const finalValue = (typeof (newPartes[index] as any)[field] === 'number') 
            ? (parseInt(value) || 0) 
            : value;

        (newPartes[index] as any)[field] = finalValue;
        
        setPartes(newPartes);
    };


    // 3. Lógica de Salvamento: ENVIA TODOS OS PATCHES DE UMA VEZ
    const handleSaveAllPartes = async () => {
        setIsSaving(true);
        setErrorPartes(null);
        let errorCount = 0;

        // Criamos uma cópia do estado para iterar, caso ele mude durante o save
        const partesToSave = [...partes]; 

        try {
            // Utilizamos Promise.all para executar as chamadas em paralelo (mais rápido)
            const savePromises = partesToSave.map(async (parte) => {
                if (parte.id) {
                    try {
                        // Enviamos a parte completa ou um payload parcial, dependendo da sua API
                        const updatePayload: Partial<ParteEmpreendimento> = {
                            nome: parte.nome,
                            area_privativa_m2: parte.area_privativa_m2,
                            valor_imovel: parte.valor_imovel,
                            disponibilidade: parte.disponibilidade,
                            quantidade_garagem: parte.quantidade_garagem,
                            origem_recurso: parte.origem_recurso,
                            forma_pagamento: parte.forma_pagamento,
                            torre: parte.torre,
                            andar: parte.andar,
                            numero_apartamento: parte.numero_apartamento,
                            atributos_extras: parte.atributos_extras,
                            // Inclua todos os campos editáveis
                        };
                        
                        await empreendimentoService.updateParteEmpreendimento(parte.id, updatePayload);
                    } catch (error) {
                        console.error(`Erro ao salvar unidade ${parte.id}:`, error);
                        errorCount++;
                    }
                }
            });

            await Promise.all(savePromises);
            
            if (errorCount > 0) {
                setErrorPartes(`Concluído com ${errorCount} erros. Verifique o console.`);
            } else {
                alert('Todas as unidades foram salvas com sucesso!');
            }
            
            // Recarrega as partes para garantir o estado final do backend
            await loadPartes();
            
        } catch (error) {
            // Este catch pega erros na Promise.all que não foram tratados individualmente
            setErrorPartes("Erro geral ao processar o salvamento das unidades.");
        } finally {
            setIsSaving(false);
        }
    };

    if (loadingPartes) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">4. Unidades</h3>
                <div className="text-center p-4 text-gray-500">Carregando unidades...</div>
            </div>
        );
    }
    
    if (errorPartes && !isSaving) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">4. Unidades</h3>
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
                    {errorPartes}
                </div>
            </div>
        );
    }

    if (!partes.length) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">4. Unidades</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Nenhuma unidade encontrada para edição.</p>
                </div>
            </div>
        )
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="px-6 py-5 border-b border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900">4. Unidades ({partes.length})</h3>
                <p className="mt-1 text-sm text-gray-500">Edite as unidades. As alterações **só serão salvas** após clicar no botão "Salvar".</p>
            </div>
            
            <div className="p-6 space-y-6">
                
                {partes.map((parte, index) => {
                    
                    // Cálculo do Valor/m²
                    const valorM2 = (
                        parte.valor_imovel && parte.area_privativa_m2 &&
                        parseFloat(String(parte.valor_imovel)) > 0 &&
                        parseFloat(String(parte.area_privativa_m2)) > 0
                    ) ? (
                        parseFloat(String(parte.valor_imovel)) / parseFloat(String(parte.area_privativa_m2))
                    ) : 0;
                    
                    return (
                        <div key={parte.id || index} className="bg-gray-50 rounded-lg p-6 border border-gray-200 relative">
                            
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">
                                Unidade {parte.nome || `${parte.numero_apartamento || 'S/N'}`}
                                <span className="inline-flex items-center ml-3 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {tiposParteOptions.find(o => o.id === parte.tipo_parte_empreendimento)?.nome || `Tipo ${parte.tipo_parte_empreendimento}`}
                                </span>
                            </h4>

                            {/* Campos principais em 3 colunas */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                                
                                {/* Nome */}
                                <div>
                                    <label htmlFor={`nome-${parte.id}`} className="block text-sm font-medium text-gray-700">Nome</label>
                                    <input
                                        type="text"
                                        id={`nome-${parte.id}`}
                                        value={parte.nome || ''}
                                        onChange={(e) => handleParteChange(index, 'nome' as keyof ParteEmpreendimentoCreate, e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                    />
                                </div>
                                
                                {/* Área Privativa (m2) */}
                                <div>
                                    <label htmlFor={`area_privativa_m2-${parte.id}`} className="block text-sm font-medium text-gray-700">Área Privativa (m²)</label>
                                    <input
                                        type="text"
                                        id={`area_privativa_m2-${parte.id}`}
                                        value={parte.area_privativa_m2 || ''}
                                        onChange={(e) => handleParteChange(index, 'area_privativa_m2' as keyof ParteEmpreendimentoCreate, e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                    />
                                </div>

                                {/* Valor do Imóvel (R$) */}
                                <div>
                                    <label htmlFor={`valor_imovel-${parte.id}`} className="block text-sm font-medium text-gray-700">Valor do Imóvel</label>
                                    <input
                                        type="text"
                                        id={`valor_imovel-${parte.id}`}
                                        value={parte.valor_imovel || ''}
                                        onChange={(e) => handleParteChange(index, 'valor_imovel' as keyof ParteEmpreendimentoCreate, e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                    />
                                </div>

                                {/* Valor/m² (Somente leitura) */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Valor/m²</label>
                                    <p className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm p-2 text-gray-600 font-semibold">
                                        {formatCurrency(valorM2, 0)}
                                    </p>
                                </div>
                            </div>
                            
                            {/* Linha 2: Localização e Pagamento */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 border-t pt-4">

                                {/* Torre */}
                                <div>
                                    <label htmlFor={`torre-${parte.id}`} className="block text-sm font-medium text-gray-700">Torre</label>
                                    <input
                                        type="number"
                                        id={`torre-${parte.id}`}
                                        value={parte.torre || ''}
                                        onChange={(e) => handleParteChange(index, 'torre' as keyof ParteEmpreendimentoCreate, e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                    />
                                </div>

                                {/* Andar */}
                                <div>
                                    <label htmlFor={`andar-${parte.id}`} className="block text-sm font-medium text-gray-700">Andar</label>
                                    <input
                                        type="number"
                                        id={`andar-${parte.id}`}
                                        value={parte.andar || ''}
                                        onChange={(e) => handleParteChange(index, 'andar' as keyof ParteEmpreendimentoCreate, e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                    />
                                </div>

                                {/* Número do Apartamento */}
                                <div>
                                    <label htmlFor={`numero_apartamento-${parte.id}`} className="block text-sm font-medium text-gray-700">Apt.</label>
                                    <input
                                        type="number"
                                        id={`numero_apartamento-${parte.id}`}
                                        value={parte.numero_apartamento || ''}
                                        onChange={(e) => handleParteChange(index, 'numero_apartamento' as keyof ParteEmpreendimentoCreate, e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                    />
                                </div>

                                {/* Forma de Pagamento */}
                                <div>
                                    <label htmlFor={`forma_pagamento-${parte.id}`} className="block text-sm font-medium text-gray-700">Forma de Pagamento</label>
                                    <input
                                        type="text"
                                        id={`forma_pagamento-${parte.id}`}
                                        value={parte.forma_pagamento || ''}
                                        onChange={(e) => handleParteChange(index, 'forma_pagamento' as keyof ParteEmpreendimentoCreate, e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                    />
                                </div>
                            </div>
                            
                            {/* Linha 3: Disponibilidade e outros */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 border-t pt-4 mt-4">
                                
                                {/* Disponibilidade (Select) */}
                                <div>
                                    <label htmlFor={`disponibilidade-${parte.id}`} className="block text-sm font-medium text-gray-700">Disponibilidade</label>
                                    <select
                                        id={`disponibilidade-${parte.id}`}
                                        value={parte.disponibilidade || ''}
                                        onChange={(e) => handleParteChange(index, 'disponibilidade' as keyof ParteEmpreendimentoCreate, e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                    >
                                        <option value="">Selecione...</option>
                                        {disponibilidadeOptions.map(opt => (
                                            <option key={opt.id} value={opt.id}>{opt.nome}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Quantidade de Garagem */}
                                <div>
                                    <label htmlFor={`quantidade_garagem-${parte.id}`} className="block text-sm font-medium text-gray-700">Vagas Garagem</label>
                                    <input
                                        type="number"
                                        id={`quantidade_garagem-${parte.id}`}
                                        value={parte.quantidade_garagem || ''}
                                        onChange={(e) => handleParteChange(index, 'quantidade_garagem' as keyof ParteEmpreendimentoCreate, e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                    />
                                </div>

                            </div>
                            
                            {/* Seção de Atributos Extras (Mapeamento Dinâmico) */}
                            {parte.atributos_extras && Object.keys(parte.atributos_extras).length > 0 && (
                                <div className="space-y-2 md:col-span-4 pt-4 border-t mt-4">
                                    <h5 className="text-sm font-medium text-gray-600 uppercase">Atributos Extras</h5>
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        {Object.entries(parte.atributos_extras).map(([key, value]) => (
                                            <div key={key}>
                                                <label htmlFor={`extra-${key}-${parte.id}`} className="block text-sm font-medium text-gray-700">{formatAttributeLabel(key)}</label>
                                                <input
                                                    type="text"
                                                    id={`extra-${key}-${parte.id}`}
                                                    value={String(value) || ''}
                                                    onChange={(e) => {
                                                        const newExtras = {...parte.atributos_extras, [key]: e.target.value};
                                                        handleParteChange(index, 'atributos_extras' as keyof ParteEmpreendimentoCreate, newExtras);
                                                    }}
                                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            
            {/* NOVO BOTÃO DE SALVAR */}
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
                <button
                    type="button"
                    onClick={handleSaveAllPartes}
                    disabled={isSaving || loadingPartes}
                    className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                >
                    {isSaving ? 'Salvando...' : 'Salvar todas as Unidades'}
                </button>
            </div>

            {/* Mensagem de erro de salvamento (se houver) */}
            {errorPartes && isSaving && (
                 <div className="p-4 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-b-xl text-sm">
                    {errorPartes}
                </div>
            )}
        </div>
    );
};

export default EmpreendimentoTipologiasForm;